import { useDetectGroupId } from '@group/hooks';
import { useEffect, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import { GROUP_LICENSE_PERMISSIONS } from 'balus-common';
import * as Sentry from '@sentry/react';
import { trpcClient } from '../../framework/trpcClient';
import { groupLicensePermissionsAtom } from './GroupLicensePermissionsAtom';
import { FullScreenLoading } from '@framework/ui';

type Props = {
  children: React.ReactNode;
};

export const WithGroupLicensePermissions: React.FC<Props> = ({ children }: Props) => {
  const groupId = useDetectGroupId();
  const setGroupLicensePermissions = useSetAtom(groupLicensePermissionsAtom);
  const lastRequestedGroupIdRef = useRef<string | null>(null);
  const [loadedGroupId, setLoadedGroupId] = useState<string | null>(null);

  useEffect(() => {
    if (groupId && groupId !== lastRequestedGroupIdRef.current) {
      lastRequestedGroupIdRef.current = groupId;

      const requestedGroupId = groupId;

      (async () => {
        let permissions: (typeof GROUP_LICENSE_PERMISSIONS)[keyof typeof GROUP_LICENSE_PERMISSIONS] | null = null;

        try {
          const result = await trpcClient.group.getGroupLicenseType.query({
            groupId: requestedGroupId,
          });
          permissions = GROUP_LICENSE_PERMISSIONS[result.type];
        } catch (e) {
          Sentry.captureException(e);
        }

        // 最新のリクエストでない場合は結果を破棄する
        if (lastRequestedGroupIdRef.current !== requestedGroupId) return;

        if (permissions) {
          setGroupLicensePermissions(permissions);
        } else {
          lastRequestedGroupIdRef.current = null;
          setGroupLicensePermissions(null);
        }
        setLoadedGroupId(requestedGroupId);
      })();
    }
  }, [groupId, setGroupLicensePermissions]);

  // グループライセンスパーミッションの読み込みが完了するまでローディングを表示する
  if (groupId && groupId !== loadedGroupId) {
    return <FullScreenLoading />;
  }

  return <>{children}</>;
};
